import React, { useState, useEffect, useCallback } from 'react';
import ProductPurchaseControl from './ProductPurchaseControl';
import { submissionKey, mapPurchaseHttpError } from '../../lib/purchaseMode';

/**
 * GOV13-A3 / Step 5 Slice 4 — Business-plane add-on surface (Plan & Capabilities).
 *
 * Server-authoritative: each add-on is annotated by the backend purchase-mode authority
 * (/billing/me/commercial/purchasable → resolve_purchase_mode). The frontend renders STRICTLY from
 * purchase_mode — a normal add-on shows Purchase (direct Stripe TEST Checkout), an exceptional add-on shows
 * Request review (governed request route), already-active/included/ineligible/unavailable show state only. The
 * browser NEVER activates entitlement or marks purchased locally; fulfilment is the signed webhook. No generic
 * "My Requests" table — an exceptional product carries its own governed status inline. No provider/internal IDs.
 */
export default function BusinessAddonRequests({ makeAuthenticatedRequest }) {
  const [addons, setAddons] = useState([]);
  const [canPurchase, setCanPurchase] = useState(false);
  const [state, setState] = useState('loading');   // loading | ok | expired | error
  const [toast, setToast] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    setState('loading'); setToast(null);
    try {
      const r = await makeAuthenticatedRequest('/api/v1/billing/me/commercial/purchasable');
      if (r && r.status === 401) { setState('expired'); return; }
      if (!r || !r.ok) { setState('error'); return; }
      const d = await r.json();
      setAddons(d.add_ons || []);
      setCanPurchase(!!d.purchase_controls_enabled);
      setState('ok');
    } catch (_) { setState('error'); }
  }, [makeAuthenticatedRequest]);
  useEffect(() => { load(); }, [load]);

  // DIRECT self-service Checkout. The browser only navigates to the returned Stripe TEST URL — it never marks
  // purchased, grants entitlement, or creates a receipt. Fulfilment is the signed webhook + a later refetch.
  const purchase = async (env) => {
    const key = submissionKey(env);
    if (busyKey) return;                          // one in-flight submission (double-click guard)
    setBusyKey(key); setToast(null);
    try {
      const res = await makeAuthenticatedRequest('/api/v1/billing/addon/checkout', {
        method: 'POST',
        body: JSON.stringify({ addon_id: env.product_key, add_on_version_id: env.product_version,
                               quantity: env.quantity || 1, origin_url: window.location.origin }),
      });
      if (res.status === 401) { setState('expired'); return; }
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        const m = mapPurchaseHttpError(res.status, d?.detail?.code, d?.detail?.reason_message || d?.detail?.message);
        setToast({ ok: false, msg: m.message });
        return;
      }
      const d = await res.json();
      if (d.url) { window.location.assign(d.url); return; }   // safe navigation to Stripe TEST Checkout
      setToast({ ok: true, msg: 'Checkout is being prepared…' });
    } catch (_) { setToast({ ok: false, msg: 'Network error. Please retry.' }); }
    finally { setBusyKey(null); }
  };

  // Exceptional (REQUEST_REQUIRED) products only — governed request; grants nothing, charges nothing.
  const requestReview = async (env) => {
    const key = submissionKey(env);
    if (busyKey) return;
    setBusyKey(key); setToast(null);
    try {
      const res = await makeAuthenticatedRequest('/api/v1/billing/me/commercial/add-ons/request', {
        method: 'POST',
        body: JSON.stringify({ add_on_version_id: env.product_version, quantity: env.quantity || 1, terms_acknowledged: true }),
      });
      if (res.status === 401) { setState('expired'); return; }
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        const m = mapPurchaseHttpError(res.status, d?.detail?.code, d?.detail?.message);
        setToast({ ok: false, msg: m.message });
        return;
      }
      await load();
      setToast({ ok: true, msg: 'Request submitted for review. This does not activate the add-on or charge you.' });
    } catch (_) { setToast({ ok: false, msg: 'Network error. Please retry.' }); }
    finally { setBusyKey(null); }
  };

  if (state === 'loading') return <p className="text-sm text-gray-400">Loading add-ons…</p>;
  if (state === 'expired') return <p className="text-sm text-amber-700">Your session has expired. Please sign in again.</p>;
  if (state === 'error') return <p className="text-sm text-red-700">Add-on information is temporarily unavailable. This is a loading problem, not a change to your plan.</p>;

  return (
    <div className="space-y-5">
      {toast && <div className={`p-3 rounded-lg border text-sm ${toast.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`} role="status">{toast.msg}</div>}
      <section>
        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Add-ons</h4>
        {addons.length === 0 ? <p className="text-sm text-gray-500">No add-ons are available for your plan right now.</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {addons.map((env) => (
              <div key={env.product_key} className="border rounded p-3">
                <div className="flex justify-between items-start">
                  <p className="font-medium text-gray-900">{env.display_name || env.product_key}</p>
                </div>
                <ProductPurchaseControl env={env} canPurchase={canPurchase} busyKey={busyKey}
                  onPurchase={purchase} onRequestReview={requestReview} />
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-400 mt-2">Purchasing a normal add-on opens a secure checkout; it activates only after a verified payment. A review-required add-on submits a governed request — it never activates or charges you on submission.</p>
      </section>
    </div>
  );
}
